import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { useQuery } from '@apollo/client'
import { Helmet } from 'react-helmet'
import Dialog from '@material-ui/core/Dialog'
import AppBar from '@material-ui/core/AppBar'
import Toolbar from '@material-ui/core/Toolbar'
import Typography from '@material-ui/core/Typography'
import IconButton from '@material-ui/core/IconButton'
import CloseIcon from '@material-ui/icons/Close'
import LinearProgress from '@material-ui/core/LinearProgress'
import Box from '@material-ui/core/Box'
import { makeStyles } from '@material-ui/core/styles'
import Slide from '@material-ui/core/Slide'
import Error from 'components/error'
import EditEventForm from 'pages/pga-coach/bookings/events/edit/edit-event-form'
import GET_GROUP_EVENT_BY_SLUG from 'pages/pga-coach/bookings/events/edit/query'
import { emitScheduleBookingsRefetch } from './schedule-bookings-refetch'

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />
})

const useStyles = makeStyles(theme => ({
  appBar: {
    position: 'relative',
  },
  title: {
    marginLeft: theme.spacing(2),
    flex: 1,
  },
  content: {
    flex: 1,
    overflowY: 'auto',
    backgroundColor: theme.palette.background.default,
  },
}))

/**
 * Full-screen editor for a group event, hosted on the schedule page (no
 * redirect to /pga-coach/bookings/events/edit/...). Reuses the standard
 * `EditEventForm` with `embedInSchedule` so save/cancel return here.
 */
const ScheduleQuickEditEventDialog = ({ open, slug, onClose }) => {
  const classes = useStyles()
  const [formKey, setFormKey] = useState(0)
  const { data, loading, error } = useQuery(GET_GROUP_EVENT_BY_SLUG, {
    variables: { slug },
    skip: !open || !slug,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  })

  const event = data?.currentUser?.groupEvents?.[0]
  const coach = data?.currentUser?.attributes
  const coachFacilities = coach?.coachFacilities || []
  const paymentAccounts = coach?.paymentAccounts || []

  const handleSaved = () => {
    emitScheduleBookingsRefetch()
    setFormKey(k => k + 1)
    onClose()
  }

  const showForm = !loading && !error && event

  return (
    <Dialog
      fullScreen
      open={open}
      onClose={onClose}
      TransitionComponent={Transition}
      keepMounted
      aria-labelledby="schedule-quick-edit-event-title"
    >
      <Helmet>
        <script
          src="https://widget.cloudinary.com/v2.0/global/all.js"
          type="text/javascript"
        />
      </Helmet>
      <AppBar color="default" elevation={1} className={classes.appBar}>
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={onClose}
            aria-label="Close"
          >
            <CloseIcon />
          </IconButton>
          <Typography
            id="schedule-quick-edit-event-title"
            variant="h6"
            className={classes.title}
          >
            Edit event
          </Typography>
        </Toolbar>
      </AppBar>
      <Box className={classes.content}>
        {loading && <LinearProgress color="primary" />}
        {error && <Error error={error} />}
        {showForm && (
          <EditEventForm
            key={`${slug}-${formKey}`}
            event={event}
            coachFacilities={coachFacilities}
            paymentAccounts={paymentAccounts}
            embedInSchedule
            onSaveSuccess={handleSaved}
            onCancel={onClose}
          />
        )}
        {!loading && !error && open && slug && !event && (
          <Box p={3}>
            <Typography color="textSecondary" variant="body1">
              Could not load this event. It may have been removed.
            </Typography>
          </Box>
        )}
      </Box>
    </Dialog>
  )
}

ScheduleQuickEditEventDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  slug: PropTypes.string,
  onClose: PropTypes.func.isRequired,
}

ScheduleQuickEditEventDialog.defaultProps = {
  slug: null,
}

export default ScheduleQuickEditEventDialog
